/**
 * Theme adapter for Virgin AU site — injects real next/font families into the foundation.
 */
import type { Theme } from '@emotion/react';
import { buildTheme } from 'common-ui';
import type { CommonThemeVars, ThemeCreateFunction } from 'tsconfig/types.d';
import { gothamFont, montserratFont, robotoFont } from '../common/nextFont';
import {
  buildThemeVarsAPACVir as buildThemeVarsAPACVirFoundation,
  buildThemeMixinsAPACVir as buildThemeMixinsAPACVirFoundation,
  buildThemeWidgetStylesAPACVir as buildThemeWidgetStylesAPACVirFoundation,
  buildThemeWidgetStylesAPACVirFromBase,
  buildThemeWidgetStylesAPACLWFromBase,
  buildCoreWidgets,
  buildProductCardWidgets,
  buildProductWidgets,
  buildSearchWidgets,
  buildMiniCartWidgets,
  buildCartWidgets,
  buildAccountWidgets,
  buildLandingpageWidgets,
} from './themeFoundation';

export const buildThemeVarsAPACVir = (
  vars: Pick<Theme, CommonThemeVars>,
): ReturnType<typeof buildThemeVarsAPACVirFoundation> =>
  buildThemeVarsAPACVirFoundation(vars, {
    montserrat: montserratFont.style.fontFamily,
    gotham: gothamFont.style.fontFamily,
    roboto: robotoFont.style.fontFamily,
  });

export const buildThemeMixinsAPACVir = (
  vars: Omit<Theme, 'mixins' | 'widgets'>,
): ReturnType<typeof buildThemeMixinsAPACVirFoundation> => buildThemeMixinsAPACVirFoundation(vars);

export const buildThemeWidgetStylesAPACVir = (
  vars: Omit<Theme, 'widgets'>,
): ReturnType<typeof buildThemeWidgetStylesAPACVirFoundation> => buildThemeWidgetStylesAPACVirFoundation(vars);

// CMS pages: core + product cards + minicart (header)
export const createCmsPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVir, buildThemeMixinsAPACVir, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVirFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductCardWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// PDP: core + full product + minicart (header)
export const createProductTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVir, buildThemeMixinsAPACVir, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVirFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Search/PLP: core + search + product cards + minicart
export const createSearchTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVir, buildThemeMixinsAPACVir, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVirFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildSearchWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Cart: core + cart + minicart
export const createCartTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVir, buildThemeMixinsAPACVir, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVirFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildCartWidgets(vars),
      }),
    ),
  );

// Account: core + account + minicart (header)
export const createAccountTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVir, buildThemeMixinsAPACVir, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVirFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildAccountWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Landing/offer pages: core + landing page + minicart
export const createLandingPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVir, buildThemeMixinsAPACVir, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVirFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildLandingpageWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

const createTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVir, buildThemeMixinsAPACVir, buildThemeWidgetStylesAPACVir);
export default createTheme;
